import React from 'react';
import Image from 'next/image';

import { Box } from '@nerdwallet/currency/components/Box';
import classes from '@nerdwallet/base-styles/classes';
import { ClassValue } from '@nerdwallet/base-styles/react';
import { colors } from '@nerdwallet/base-styles/variables';
import { ColorToken } from '@nerdwallet/base-styles/tokens';

import styles from './base-icon.module.scss';

export enum IconSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export enum Themes {
  DEFAULT = 'DEFAULT',
  BLUE_WHITE = 'BLUE_WHITE',
  GREEN_WHITE = 'GREEN_WHITE',
  YELLOW_WHITE = 'YELLOW_WHITE',
  YELLOW = 'YELLOW',
}

type Config = {
  paddingClassname: ClassValue;
  // We need to distinguish between image size and icon size because *icons*
  // have extra padding, whereas images don't.
  imageSize: number;
  iconSize: number;
};

const config: Record<IconSize, Config> = {
  SMALL: {
    paddingClassname: 'padding-all-1',
    imageSize: 24,
    iconSize: 16,
  },
  MEDIUM: {
    paddingClassname: 'padding-all-2',
    imageSize: 36,
    iconSize: 20,
  },
  LARGE: {
    paddingClassname: 'padding-all-3',
    imageSize: 66,
    iconSize: 36,
  },
};

type Theme = {
  backgroundColor: ColorToken;
  iconColor: ColorToken;
};

const themes: Record<Themes, Theme> = {
  DEFAULT: {
    backgroundColor: 'transparent',
    iconColor: 'neutral-darkest',
  },
  BLUE_WHITE: {
    backgroundColor: 'blue-dark',
    iconColor: 'white',
  },
  GREEN_WHITE: {
    backgroundColor: 'green-light',
    iconColor: 'white',
  },
  YELLOW_WHITE: {
    backgroundColor: 'yellow-dark',
    iconColor: 'white',
  },
  YELLOW: {
    backgroundColor: 'yellow-lightest',
    iconColor: 'yellow-dark',
  },
};

const BaseIcon: React.FC<BaseIconProps> = ({
  altText,
  Icon,
  size,
  src,
  theme = Themes.DEFAULT,
}) => {
  const { paddingClassname, imageSize, iconSize } = config[size];
  const { backgroundColor, iconColor } = themes[theme];

  return (
    <Box backgroundColor={backgroundColor} className={styles.container}>
      {src ? (
        <Image
          className={styles.icon}
          src={src}
          alt={altText}
          width={imageSize}
          height={imageSize}
        />
      ) : (
        <div className={classes(paddingClassname)}>
          {/* @ts-expect-error Icon is a react-icon, but can't deduce types */}
          <Icon color={colors[iconColor]} size={iconSize} />
        </div>
      )}
    </Box>
  );
};

interface BaseIconProps {
  altText?: string;
  Icon?: React.ComponentClass;
  size: IconSize;
  src?: string;
  theme?: keyof typeof Themes;
}

export default BaseIcon;
